import { PartyType } from '@parties/src/types';
import { useQuery } from 'react-query';
import { wgApi } from '../../../../../app/src/features/api/wgApi';

interface AnvaFlowsGroupedCoveragesResponse {
  items: AnvaFlowsGroupedCoverage[];
  count: number;
}

export interface AnvaFlowsGroupedCoverage {
  adn_code: string;
  name: string;
  mandatory: boolean;
  coverage_codes: string[];
}

// Legacy interface for backward compatibility
export interface AnvaFlowsCoverage {
  code: string;
  name: string;
  mandatory: boolean;
}

export const getAnvaFlowsCoverages = async (
  partyType: PartyType,
  insurancesIds?: number[],
) => {
  const { data, isError } = await wgApi.get<AnvaFlowsGroupedCoveragesResponse>(
    `/v1/api/anvaflows/supported-adn-coverages`,
    {
      query: {
        insurance_ids: insurancesIds,
        party_type: partyType,
      },
    },
  );
  if (isError) throw new Error();
  return data;
};

// Helper function to flatten coverage codes from grouped coverages
export const flattenCoverageCodes = (
  groupedCoverages: AnvaFlowsGroupedCoverage[],
): string[] => {
  return groupedCoverages.reduce((acc: string[], group) => {
    return acc.concat(group.coverage_codes);
  }, []);
};

// Helper function to convert grouped coverages to legacy format for compatibility
export const convertToLegacyFormat = (
  groupedCoverages: AnvaFlowsGroupedCoverage[],
): AnvaFlowsCoverage[] => {
  return groupedCoverages.flatMap((group) =>
    group.coverage_codes.map((code) => ({
      code,
      name: `${group.name} - ${code}`,
      mandatory: group.mandatory,
    })),
  );
};

const getUseAnvaFlowsCoveragesQkey = (
  partyType: PartyType,
  insuranceIds?: number[],
) => ['flow-config', 'anva', 'grouped-coverages', partyType, insuranceIds];

export const useAnvaFlowsCoverages = (
  partyType: PartyType,
  insuranceIds?: number[],
) => {
  return useQuery(
    getUseAnvaFlowsCoveragesQkey(partyType, insuranceIds),
    () => getAnvaFlowsCoverages(partyType, insuranceIds),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !!insuranceIds,
    },
  );
};
